import {
  SWAP_RATE_NAME_TO_PLATFORM_FEE_PERCENT,
  PLATFORM_FEE_CALCULATION_RATE,
  SWAP_MIN_AMOUNTS,
  SystemCurrency,
} from '@okletsplay/core';
import BigNumber from 'bignumber.js';
import { calculatePlatformFeeFromPrimaryAmount, convertCrypto } from './currency';

const SWAP_FEE_PERCENT = SWAP_RATE_NAME_TO_PLATFORM_FEE_PERCENT[PLATFORM_FEE_CALCULATION_RATE];
const SWAP_MIN_LIMIT = SWAP_MIN_AMOUNTS[SystemCurrency.USDT];
export const QUICK_SWAP_BUFFER_PERCENT = 10;

interface CalculateBuyAmountToSwapToCoverPlatformFeeCurrency {
  mainCurrencyAmount: number;
  platformFeeCurrencyBalance: number;
  mainCurrencyBalance: number;
  exchangeRate: number;
  gameActivityFeePercentage: number;
}

interface Result {
  hasEnoughBalance: boolean;
  suggestedSwapBuyAmount: number;
}

const amountPlusPercent = (rawAmount: number | BigNumber, percentage: number): BigNumber => {
  const amount = new BigNumber(rawAmount);
  return amount.plus(amount.multipliedBy(percentage).dividedBy(100));
};

const notLessThanMinSwap = (amount: BigNumber, exchangeRate: number): BigNumber => {
  const convertedMinAmount = convertCrypto(SWAP_MIN_LIMIT, exchangeRate);

  if (amount.lt(convertedMinAmount)) return new BigNumber(convertedMinAmount);

  return amount;
};

export const calculateBuyAmountToSwapToCoverPlatformFeeCurrency = ({
  mainCurrencyAmount,
  platformFeeCurrencyBalance,
  mainCurrencyBalance,
  exchangeRate,
  gameActivityFeePercentage,
}: CalculateBuyAmountToSwapToCoverPlatformFeeCurrency): Result => {
  const amountInPlatformFeeCurrency = new BigNumber(
    calculatePlatformFeeFromPrimaryAmount(
      mainCurrencyAmount,
      exchangeRate,
      gameActivityFeePercentage
    )
  );

  if (amountInPlatformFeeCurrency.lte(platformFeeCurrencyBalance)) {
    return {
      hasEnoughBalance: true,
      suggestedSwapBuyAmount: 0,
    };
  }

  let amountToSwap: BigNumber;

  const amountNeededMinusPlatformFeeCurrencyBalance = amountInPlatformFeeCurrency.minus(
    platformFeeCurrencyBalance
  );

  const amountToSwapWithFee = amountPlusPercent(
    notLessThanMinSwap(amountNeededMinusPlatformFeeCurrencyBalance, exchangeRate),
    SWAP_FEE_PERCENT
  );
  const amountToSwapWithFeeAndBuffer = amountPlusPercent(
    notLessThanMinSwap(amountToSwapWithFee, exchangeRate),
    QUICK_SWAP_BUFFER_PERCENT
  );

  const mainCurrencyRemainingBalanceInPlatformFeeCurrency = convertCrypto(
    mainCurrencyBalance - mainCurrencyAmount,
    exchangeRate
  );

  if (amountToSwapWithFee.gt(mainCurrencyRemainingBalanceInPlatformFeeCurrency)) {
    return {
      hasEnoughBalance: false,
      suggestedSwapBuyAmount: 0,
    };
  } else if (amountToSwapWithFeeAndBuffer.lte(mainCurrencyRemainingBalanceInPlatformFeeCurrency)) {
    amountToSwap = amountPlusPercent(
      notLessThanMinSwap(amountNeededMinusPlatformFeeCurrencyBalance, exchangeRate),
      QUICK_SWAP_BUFFER_PERCENT
    );
  } else {
    amountToSwap = notLessThanMinSwap(amountNeededMinusPlatformFeeCurrencyBalance, exchangeRate);
  }

  return {
    hasEnoughBalance: true,
    suggestedSwapBuyAmount: Math.round(amountToSwap.toNumber()),
  };
};
